// ===== Types =====
export interface UserProfile {
  id: string;
  name: string;
  email: string;
  avatar: string;
  memberSince: string;
  totalInvested: number;
  totalMined: number;
  referralCode: string;
  twoFactorEnabled: boolean;
  notificationsEnabled: boolean;
}

export interface PoolSummary {
  id: string;
  name: string;
  gpuModel: string;
  gpuCount: number;
  status: "mining" | "collecting" | "completed";
  targetAmount: number;
  collectedAmount: number;
  participantCount: number;
  hashrate: string;
  monthlyYield: number; // percentage
  dailyRewardGNK: number;
  imageColor: string;
}

export interface PoolDetail extends PoolSummary {
  description: string;
  members: PoolMember[];
  rewardHistory: RewardEntry[];
  totalMinedGNK: number;
  startDate: string;
  userShare: number; // percentage
  userInvested: number;
  userEarnings: number;
}

export interface PoolMember {
  id: string;
  name: string;
  avatar: string;
  invested: number;
  share: number;
}

export interface RewardEntry {
  date: string;
  amount: number;
  pool: string;
}

export interface WalletData {
  balance: number;
  pendingRewards: number;
  todayEarnings: number;
  equivalentRub: number;
  gnkPriceRub: number;
  transactions: Transaction[];
}

export interface Transaction {
  id: string;
  type: "mining" | "withdrawal" | "bonus" | "deposit";
  amount: number;
  currency: "GNK" | "RUB";
  date: string;
  description: string;
  status: "completed" | "pending";
}

export interface ReferralData {
  code: string;
  link: string;
  totalInvited: number;
  totalEarned: number;
  bonusPerReferral: number;
  referrals: Referral[];
}

export interface Referral {
  id: string;
  name: string;
  avatar: string;
  joinDate: string;
  bonusEarned: number;
  level: number;
}

export interface DashboardData {
  totalInvested: number;
  totalMined: number;
  activePools: number;
  currentYield: number;
  recentActivity: ActivityItem[];
  userPools: PoolSummary[];
}

export interface ActivityItem {
  id: string;
  type: "reward" | "deposit" | "referral" | "pool_activated";
  description: string;
  amount?: number;
  currency?: string;
  date: string;
  icon: string;
}

// ===== Mock Data =====

export function getUser(): UserProfile {
  return {
    id: "u1",
    name: "Алексей Петров",
    email: "alexey@example.com",
    avatar: "АП",
    memberSince: "2025-11-15",
    totalInvested: 187500,
    totalMined: 3842.67,
    referralCode: "ALEX2025",
    twoFactorEnabled: true,
    notificationsEnabled: true,
  };
}

export function getPools(): PoolSummary[] {
  return [
    {
      id: "p1",
      name: "RTX 4090 Alpha",
      gpuModel: "NVIDIA RTX 4090",
      gpuCount: 4,
      status: "mining",
      targetAmount: 960000,
      collectedAmount: 960000,
      participantCount: 12,
      hashrate: "520 MH/s",
      monthlyYield: 8.5,
      dailyRewardGNK: 42.8,
      imageColor: "#dc2626",
    },
    {
      id: "p2",
      name: "RTX 4080 Beta",
      gpuModel: "NVIDIA RTX 4080 SUPER",
      gpuCount: 6,
      status: "mining",
      targetAmount: 720000,
      collectedAmount: 720000,
      participantCount: 18,
      hashrate: "410 MH/s",
      monthlyYield: 7.2,
      dailyRewardGNK: 34.5,
      imageColor: "#ef4444",
    },
    {
      id: "p3",
      name: "RTX 5090 Gamma",
      gpuModel: "NVIDIA RTX 5090",
      gpuCount: 2,
      status: "collecting",
      targetAmount: 800000,
      collectedAmount: 534000,
      participantCount: 8,
      hashrate: "680 MH/s",
      monthlyYield: 11.3,
      dailyRewardGNK: 58.2,
      imageColor: "#b91c1c",
    },
    {
      id: "p4",
      name: "RTX 4070 Ti Delta",
      gpuModel: "NVIDIA RTX 4070 Ti SUPER",
      gpuCount: 8,
      status: "collecting",
      targetAmount: 640000,
      collectedAmount: 198000,
      participantCount: 5,
      hashrate: "340 MH/s",
      monthlyYield: 6.8,
      dailyRewardGNK: 28.1,
      imageColor: "#f87171",
    },
    {
      id: "p5",
      name: "RTX 4090 Epsilon",
      gpuModel: "NVIDIA RTX 4090",
      gpuCount: 4,
      status: "completed",
      targetAmount: 960000,
      collectedAmount: 960000,
      participantCount: 15,
      hashrate: "520 MH/s",
      monthlyYield: 8.5,
      dailyRewardGNK: 0,
      imageColor: "#666666",
    },
    {
      id: "p6",
      name: "A100 Omega",
      gpuModel: "NVIDIA A100 80GB",
      gpuCount: 2,
      status: "collecting",
      targetAmount: 1200000,
      collectedAmount: 420000,
      participantCount: 6,
      hashrate: "890 MH/s",
      monthlyYield: 14.1,
      dailyRewardGNK: 72.4,
      imageColor: "#991b1b",
    },
  ];
}

export function getPoolDetail(id: string): PoolDetail {
  const pool = getPools().find((p) => p.id === id);
  if (!pool) return getPoolDetail("p1");

  return {
    ...pool,
    description: "Совместный майнинг-пул для добычи токенов GNK через сеть Gonka.ai. Proof of Compute консенсус обеспечивает стабильный доход от GPU-вычислений.",
    totalMinedGNK: pool.status === "mining" ? 12845.32 : pool.status === "completed" ? 28450.0 : 0,
    startDate: pool.status === "mining" ? "2026-01-15" : pool.status === "completed" ? "2025-06-10" : "",
    userShare: 14.2,
    userInvested: Math.round(pool.targetAmount * 0.142),
    userEarnings: pool.status === "mining" ? 1824.08 : pool.status === "completed" ? 4040.3 : 0,
    members: [
      { id: "m1", name: "Алексей П.", avatar: "АП", invested: Math.round(pool.targetAmount * 0.142), share: 14.2 },
      { id: "m2", name: "Мария К.", avatar: "МК", invested: Math.round(pool.targetAmount * 0.118), share: 11.8 },
      { id: "m3", name: "Дмитрий С.", avatar: "ДС", invested: Math.round(pool.targetAmount * 0.095), share: 9.5 },
      { id: "m4", name: "Ольга В.", avatar: "ОВ", invested: Math.round(pool.targetAmount * 0.087), share: 8.7 },
      { id: "m5", name: "Игорь Н.", avatar: "ИН", invested: Math.round(pool.targetAmount * 0.076), share: 7.6 },
      { id: "m6", name: "Анна Л.", avatar: "АЛ", invested: Math.round(pool.targetAmount * 0.068), share: 6.8 },
    ],
    rewardHistory: [
      { date: "2026-04-12", amount: 6.08, pool: pool.name },
      { date: "2026-04-11", amount: 5.94, pool: pool.name },
      { date: "2026-04-10", amount: 6.21, pool: pool.name },
      { date: "2026-04-09", amount: 5.87, pool: pool.name },
      { date: "2026-04-08", amount: 6.15, pool: pool.name },
      { date: "2026-04-07", amount: 5.76, pool: pool.name },
      { date: "2026-04-06", amount: 6.32, pool: pool.name },
    ],
  };
}

export function getWallet(): WalletData {
  return {
    balance: 3842.67,
    pendingRewards: 12.45,
    todayEarnings: 6.08,
    equivalentRub: 384267,
    gnkPriceRub: 100,
    transactions: [
      { id: "t1", type: "mining", amount: 6.08, currency: "GNK", date: "2026-04-12", description: "RTX 4090 Alpha", status: "completed" },
      { id: "t2", type: "mining", amount: 4.92, currency: "GNK", date: "2026-04-12", description: "RTX 4080 Beta", status: "completed" },
      { id: "t3", type: "bonus", amount: 25.0, currency: "GNK", date: "2026-04-11", description: "Реферальный бонус — Игорь Н.", status: "completed" },
      { id: "t4", type: "mining", amount: 5.94, currency: "GNK", date: "2026-04-11", description: "RTX 4090 Alpha", status: "completed" },
      { id: "t5", type: "withdrawal", amount: -500.0, currency: "GNK", date: "2026-04-10", description: "Вывод на 0x4a2e...8f3c", status: "completed" },
      { id: "t6", type: "mining", amount: 6.21, currency: "GNK", date: "2026-04-10", description: "RTX 4090 Alpha", status: "completed" },
      { id: "t7", type: "deposit", amount: 50000, currency: "RUB", date: "2026-04-09", description: "Вклад в RTX 5090 Gamma", status: "completed" },
      { id: "t8", type: "mining", amount: 4.88, currency: "GNK", date: "2026-04-09", description: "RTX 4080 Beta", status: "completed" },
      { id: "t9", type: "mining", amount: 5.87, currency: "GNK", date: "2026-04-09", description: "RTX 4090 Alpha", status: "pending" },
      { id: "t10", type: "bonus", amount: 25.0, currency: "GNK", date: "2026-04-08", description: "Реферальный бонус — Анна Л.", status: "completed" },
    ],
  };
}

export function getReferrals(): ReferralData {
  return {
    code: "ALEX2025",
    link: "https://gnk.gonka.ai/ref/ALEX2025",
    totalInvited: 7,
    totalEarned: 175.0,
    bonusPerReferral: 25,
    referrals: [
      { id: "r1", name: "Игорь Новиков", avatar: "ИН", joinDate: "2026-04-11", bonusEarned: 25.0, level: 1 },
      { id: "r2", name: "Анна Леонова", avatar: "АЛ", joinDate: "2026-04-08", bonusEarned: 25.0, level: 1 },
      { id: "r3", name: "Сергей Морозов", avatar: "СМ", joinDate: "2026-03-25", bonusEarned: 25.0, level: 1 },
      { id: "r4", name: "Екатерина Волкова", avatar: "ЕВ", joinDate: "2026-03-12", bonusEarned: 25.0, level: 2 },
      { id: "r5", name: "Павел Кузнецов", avatar: "ПК", joinDate: "2026-02-28", bonusEarned: 25.0, level: 1 },
      { id: "r6", name: "Наталья Соколова", avatar: "НС", joinDate: "2026-02-15", bonusEarned: 25.0, level: 2 },
      { id: "r7", name: "Роман Фёдоров", avatar: "РФ", joinDate: "2026-01-20", bonusEarned: 25.0, level: 1 },
    ],
  };
}

export function getDashboard(): DashboardData {
  const pools = getPools();
  return {
    totalInvested: 187500,
    totalMined: 3842.67,
    activePools: 2,
    currentYield: 7.85,
    userPools: pools.filter((p) => ["p1", "p2", "p3"].includes(p.id)),
    recentActivity: [
      { id: "a1", type: "reward", description: "Начисление GNK из RTX 4090 Alpha", amount: 6.08, currency: "GNK", date: "2026-04-12", icon: "⛏" },
      { id: "a2", type: "reward", description: "Начисление GNK из RTX 4080 Beta", amount: 4.92, currency: "GNK", date: "2026-04-12", icon: "⛏" },
      { id: "a3", type: "referral", description: "Новый реферал — Игорь Новиков", amount: 25.0, currency: "GNK", date: "2026-04-11", icon: "👥" },
      { id: "a4", type: "deposit", description: "Вклад в RTX 5090 Gamma", amount: 50000, currency: "RUB", date: "2026-04-09", icon: "💳" },
      { id: "a5", type: "pool_activated", description: "Пул RTX 4080 Beta активирован", date: "2026-03-20", icon: "🚀" },
    ],
  };
}
